import React, { useState, FormEvent, useContext, useCallback, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  DropdownToggle,
  DropdownMenu,
  Button,
  Dropdown,
  Form,
  Input,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import OrderTable from "./orderTable/OrderTable";
import classNames from "classnames";
import BreadcrumbHeader from "components/common/BreadcrumbHeader";
import Authorize from "components/framework/authorization/Authorize";
import { SoaItemType } from "components/orders/types/SoaItemType";
import DisconnectModal from "../disconnect/DisconnectModal";
import ModifyModal from "../modify/ModifyModal";
import CreateAuditModal from "components/audits/CreateAuditModal";
import { PermissionType } from "services/authorization/PermissionType";
import { EnvironmentUtil } from "services/util/EnvironmentUtil";
import { AppContext } from "services/appContext/AppContext";
import IconWithTooltip from "components/common/IconWithTooltip";
import { getTranslation } from "translations/TranslationService";
import { CompanyClientType } from "components/manage/serviceProvider/CompanyClientType";
import SearchAndProvisionModal from "../searchAndProvision/SearchAndProvisionModal";
import { HashRouter, Route, Switch } from "react-router-dom";
import Navbar from "components/common/navBar/NavBar";
import SinchOrderTable from "./orderTable/SinchOrderTable";
import DateTimeInput from "components/framework/forms/DateTimeInput";
import { Moment } from "moment";
import { usePastedNumberFormatter } from "services/customHooks/usePastedNumberFormatter";

const isMixNetworkUrl = EnvironmentUtil.isMixNetwork

export default function OrdersList() {
  const history = useHistory();
  const [showDropdown, setShowDropdown] = useState(false);
  const [, setErrorMessageId] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [focusedSearchInput, setFocusedSearchInput] = useState(false);
  const [soaItemType, setSoaItemType] = useState<SoaItemType>();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const { appContext } = useContext(AppContext);
  const theme = appContext.theme;
  
  const onPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
  e.preventDefault();

  const pasted = e.clipboardData.getData("text").trim();
  const lines = pasted.split(/[\r\n\t, ]+/); 

  const phoneRegex = /^\d{10}$/;

  const phones: string[] = [];
  let nonPhoneData = "";

  for (const line of lines) {
    const trimmed = line.trim();

    if (phoneRegex.test(trimmed)) {
      phones.push(trimmed);
    } else if (trimmed) {
      nonPhoneData += (nonPhoneData ? " " : "") + trimmed;
    }
  }

  const allParts = [...phones];
  if (nonPhoneData) {
    allParts.push(nonPhoneData); // add raw string exactly as is (not in quotes)
  }

  const finalValue = allParts.join(",");

  handleSearchChange(finalValue);
}, [searchInputValue]);


  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const inputValue = searchInputValue.trim();
    const errorMessageIdResult = validateSearchValue();
    setErrorMessageId(errorMessageIdResult);

    if (!errorMessageIdResult) {
      setSearchValue(inputValue);      
    }
  };

  const handleSearchChange = (searchValue: string) => {
    setErrorMessageId("");
    setSearchInputValue(searchValue);
  };

  const dropdownToggle = () => setShowDropdown((prevState) => !prevState);

  const handleCreateDropdownItemButtonClick = (soaItemType: SoaItemType) => {
    setSoaItemType(soaItemType);
    setShowModal(true);
    setShowDropdown(false);

    if (soaItemType === SoaItemType.PortIn) {
      history.push("/Orders/PortIn");
    }

    if(soaItemType === SoaItemType.DisasterRecovery) {
      history.push("/Orders/DisasterRecovery")
    }

    if (soaItemType === SoaItemType.PortOut) {
      history.push("/Orders/PortOut");
    }
  };

  const navTitlesRoutes = [
    {routes: '/Orders', titles: 'PortControl'},
    {routes: '/SinchOrders', titles: 'Wholesale'},
    //Add more navigation items as needed.
  ]

  const accessRoutes = [
    {routes: '/Orders', titles: 'PortControl'}
  ]

  const routes = appContext.localStorageInfo.user?.permissions.includes(PermissionType.SinchSearchAndProvision) ? navTitlesRoutes : accessRoutes ;

  return (
    <>
      <BreadcrumbHeader>
        <FormattedMessage id="orders.title" />
      </BreadcrumbHeader>
      <Container className="mb-5">
        <Row>
          <Col md="6" className="offset-md-3">
            <h1 className={`display-3 ${theme === "light" ? "lblue-color" : "pc-soa-order-bg"}`}>
              <FormattedMessage id="orders.title" />
              <IconWithTooltip
                className="fas fa-info-circle cursor-pointer mx-2 icon-font-size"
                
                tooltipClassName="custom-tooltip-background"
                tooltip={getTranslation(intl, (appContext.localStorageInfo.selectedProfile?.clientType === CompanyClientType.PORTCONTROL 
                  ? (appContext.localStorageInfo.selectedProfile?.external
                      ? "vpopuserOrders.info.tooltip"
                      : "orders.info.tooltip"
                    )
                  : "wholesaleOrders.info.tooltip"))}
                />
            </h1>
          </Col>
        </Row>
        <Row>
        {<Col md="3"></Col>}
        <Col md={isMixNetworkUrl ? "6" : "6"} xs="12" className="mb-1">
            <Form onSubmit={handleSubmit}>
              <FormGroup
                className={classNames("input-group-merge pc-search-container mb-0", {
                  "pc-orders-container": !isMixNetworkUrl,
                  focused: focusedSearchInput
                })}>
                <InputGroup className="input-group-merge">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="text"
                    className="pc-orders-search"
                    placeholder={intl.formatMessage({ id: "orders.search.placeholder" })}
                    value={searchInputValue}
                    onChange={(e) => handleSearchChange(e.currentTarget.value)}
                    onFocus={() => setFocusedSearchInput(true)}
                    onBlur={() => setFocusedSearchInput(false)}
                    onPaste={onPaste}
                  />
                </InputGroup>                
                <Button color={`${theme === "dark" ? "danger" : ""}`} disabled={isLoading} type="submit" className={`pc-btn ${isMixNetworkUrl ? 'ml-4' : ''} ${theme === "light" ? "bg-lblue" : ""}`}>
                  {isLoading ? "Loading..." : <FormattedMessage id="orders.search" />}
                </Button>
              </FormGroup>
            </Form>
          </Col>
          <Col md="3" xs="12" className="mb-1 pl-2">
            <Authorize spidPermissions={[PermissionType.SOAWrite, PermissionType.PreOrderFree]}>
              <div className="pc-create-dropdown">
                <Dropdown isOpen={showDropdown} toggle={dropdownToggle}>
                  <DropdownToggle className="cursor-pointer pb-0 pt-0v" tag="span">
                    <Button color={`${theme === "dark" ? "danger" : ""}`} className={`pc-btn create-btn ${theme === "light" ? "bg-lblue" : ""}`}>
                      <i className="fas fa-plus-square mr-2" />
                      <FormattedMessage id="orders.create" />
                    </Button>
                  </DropdownToggle>
                  <DropdownMenu className={`dropdown-menu-lg dropdown-menu-white ${theme === "light" ? "bg-dropdown text-crow" : "bg-dark text-white pc-no-shadow"}`}>
                    <Row className="shortcuts px-4">
                      <Authorize
                        spidPermissions={[PermissionType.SOAWrite, PermissionType.PreOrderFree]}>
                        <Col
                          className="shortcut-item cursor-pointer"
                          onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.PortIn)}
                          xs="4">
                          <span className="shortcut-media avatar rounded-circle bg-gradient-pc-blue">
                            <i className="fa fa-arrow-left" />
                          </span>
                          <small>
                            <FormattedMessage id="orders.create.portIn" />
                          </small>
                        </Col>
                          {(isMixNetworkUrl && appContext.localStorageInfo.user?.permissions.includes(PermissionType.DisasterRecovery)) &&
                            <Col
                              className="shortcut-item cursor-pointer"
                              onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.DisasterRecovery)}
                              xs="4">
                              <span className="shortcut-media avatar rounded-circle bg-gradient-red">
                                <i className="fa fa-exclamation-triangle" />
                              </span>
                              <small>
                                <FormattedMessage id="orders.create.disasterRecovery" />
                              </small>
                            </Col>
                          }
                          {(isMixNetworkUrl && appContext.localStorageInfo.user?.permissions.includes(PermissionType.SinchSearchAndProvision)) &&
                            <Col
                              className="shortcut-item cursor-pointer"
                              onClick={() =>
                                handleCreateDropdownItemButtonClick(SoaItemType.SearchAndProvision)
                              }
                              xs="4">
                              <span className="shortcut-media avatar rounded-circle bg-gradient-pc-blue">
                                <i className="fa fa-search-plus" />
                              </span>
                              <small>
                                <FormattedMessage id="orders.create.searchAndProvision" />
                              </small>
                            </Col>
                          }
                      </Authorize>
                      {!isMixNetworkUrl && <Authorize spidPermissions={[PermissionType.SOAWrite]}>
                        <Col
                          className="shortcut-item cursor-pointer"
                          onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.PortOut)}
                          xs="4">
                          <span className="shortcut-media avatar rounded-circle bg-gradient-pc-orange">
                            <i className="fa fa-arrow-right" />
                          </span>
                          <small>
                            <FormattedMessage id="orders.create.portOut" />
                          </small>
                        </Col>
                      </Authorize>}
                    {!isMixNetworkUrl && <Authorize spidPermissions={[PermissionType.SOAWrite]}>
                        <Col
                          className="shortcut-item cursor-pointer"
                          onClick={() =>
                            handleCreateDropdownItemButtonClick(SoaItemType.Disconnect)
                          }
                          xs="4">
                          <span className="shortcut-media avatar rounded-circle bg-gradient-red">
                            <i className="fa fa-undo" />
                          </span>
                          <small>
                            <FormattedMessage id="orders.create.disconnect" />
                          </small>
                        </Col>
                        <Col
                          className="shortcut-item cursor-pointer"
                          onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.Modify)}
                          xs="4">
                          <span className="shortcut-media avatar rounded-circle bg-gradient-red">
                            <i className="fa fa-edit" />
                          </span>
                          <small>
                            <FormattedMessage id="orders.create.modify" />
                          </small>
                        </Col>
                        <Col
                          className="shortcut-item cursor-pointer"
                          onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.Audit)}
                          xs="4">
                          <span className="shortcut-media avatar rounded-circle bg-gradient-pc-blue">
                            <i className="fa fa-check-double" />
                          </span>
                          <small>
                            <FormattedMessage id="audits.create.audit" />
                          </small>
                        </Col>
                        { appContext.localStorageInfo.user?.permissions.includes(PermissionType.DisasterRecovery) &&
                          <Col
                            className="shortcut-item cursor-pointer"
                            onClick={() => handleCreateDropdownItemButtonClick(SoaItemType.DisasterRecovery)}
                            xs="4">
                            <span className="shortcut-media avatar rounded-circle bg-gradient-red">
                              <i className="fa fa-exclamation-triangle" />
                            </span>
                            <small>
                              <FormattedMessage id="orders.create.disasterRecovery" />
                            </small>
                          </Col>
                        }
                        {appContext.localStorageInfo.user?.permissions.includes("SinchSearchAndProvision") && (
                          <Col
                            className="shortcut-item cursor-pointer"
                            onClick={() =>
                              handleCreateDropdownItemButtonClick(SoaItemType.SearchAndProvision)
                            }
                            xs="4">
                            <span className="shortcut-media avatar rounded-circle bg-gradient-pc-blue">
                              <i className="fa fa-search-plus" />
                            </span>
                            <small>
                              <FormattedMessage id="orders.create.searchAndProvision" />
                            </small>
                          </Col>
                        )}
                    </Authorize>}
                    </Row>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Authorize>
          </Col>
        </Row>
      </Container>
      <Container fluid>
        {soaItemType === SoaItemType.Audit && showModal && (
          <CreateAuditModal closeModal={() => setShowModal(false)} />
        )}
        {soaItemType === SoaItemType.Disconnect && showModal && (
          <DisconnectModal closeModal={() => setShowModal(false)} />
        )}
        {soaItemType === SoaItemType.Modify && showModal && (
          <ModifyModal closeModal={() => setShowModal(false)} />
        )}
        {soaItemType === SoaItemType.SearchAndProvision && showModal && (
          <SearchAndProvisionModal closeModal={() => setShowModal(false)} />
        )}
        <HashRouter>
          <Navbar navTitlesRoutes={routes}/>
            <Switch>
              <Route path='/Orders' exact render={(props) => (
                <OrderTable
                  search={searchValue}                 
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              )}/>
              <Route path='/SinchOrders' exact render={(props) => (
                <SinchOrderTable
                  search={searchValue}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
              )}/>
            </Switch>
        </HashRouter>
      </Container>
    </>
  );
}

const validateSearchValue = (): string => {
  return "";
};
